import { gql, useMutation, useQuery } from '@apollo/client';
import { Button } from 'components/button';
import { FilterItemsTable } from 'components/content/filter-items-table';
import { Modal } from 'components/modal';
import {
  ContentFilterGroupQuery,
  CreateContentFilterItemMutation,
  CreateContentFilterItemMutationVariables,
} from 'graphql/types';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CreateFilterItem } from './create-item';
import { useNotifications } from 'notifications';
import { Loading } from 'components/loading';

const ContentFilterGroupPage = () => {
  const showNotification = useNotifications();
  const [showModal, setShowModal] = useState(false);
  const { filterGroupId } = useParams<{ filterGroupId: string }>();

  const contentFilterGroupQuery = gql`
    query ContentFilterGroup($id: ID!) {
      contentFilterGroup(id: $id) {
        id
        title
        contentTag {
          id
          name
        }
        filterItems {
          id
          title
        }
      }
    }
  `;
  const { data, loading } = useQuery<ContentFilterGroupQuery>(
    contentFilterGroupQuery,
    {
      variables: { id: filterGroupId },
    },
  );
  const filterItems = data?.contentFilterGroup?.filterItems;

  const [createFilterItem] = useMutation<
    CreateContentFilterItemMutation,
    CreateContentFilterItemMutationVariables
  >(
    gql`
      mutation CreateContentFilterItem($input: CreateContentFilterItemInput!) {
        createContentFilterItem(input: $input) {
          filterItem {
            id
            title
          }
        }
      }
    `,
    {
      update(cache, { data }) {
        if (!data?.createContentFilterItem.filterItem) return;

        const variables = { id: filterGroupId };

        const existingFilterGroup = cache.readQuery<ContentFilterGroupQuery>({
          query: contentFilterGroupQuery,
          variables,
        });
        if (!existingFilterGroup?.contentFilterGroup) return;

        cache.writeQuery<ContentFilterGroupQuery>({
          query: contentFilterGroupQuery,
          variables,
          data: {
            contentFilterGroup: {
              ...existingFilterGroup.contentFilterGroup,
              filterItems: [
                data.createContentFilterItem.filterItem,
                ...(existingFilterGroup.contentFilterGroup.filterItems ?? []),
              ],
            },
          },
        });
      },
      onCompleted: () => {
        showNotification({
          message: 'Filter item created successfully',
          type: 'success',
        });
        setShowModal(false);
      },
      onError: (error) => {
        showNotification({
          message: error.message,
          type: 'error',
        });
      },
    },
  );

  const onCreateFilterItem = ({ title }: { title: string }) => {
    createFilterItem({
      variables: {
        input: { title, filterGroupId: data?.contentFilterGroup?.id ?? '' },
      },
    });
  };

  return (
    <div>
      <div className="flex justify-between mb-4">
        <h2>
          <span className="font-bold">Filter Group: </span>
          {data?.contentFilterGroup?.title}
        </h2>
        <h2>
          <span className="font-bold">Content Category: </span>
          {data?.contentFilterGroup?.contentTag?.name}
        </h2>
      </div>
      <div className="flex justify-end mb-8">
        <Button variant="outline" onClick={() => setShowModal(true)}>
          Add Filter Item
        </Button>
      </div>
      <Modal
        show={showModal}
        width="max-w-screen-md"
        isAutoOverflow={false}
        onClose={() => setShowModal(false)}
      >
        <div className="bg-white rounded p-5">
          <CreateFilterItem
            onClose={() => setShowModal(false)}
            onCreateFilterItem={onCreateFilterItem}
            loading={loading}
          />
        </div>
      </Modal>
      <div>
        {loading ? <Loading /> : <FilterItemsTable filterItems={filterItems} />}
      </div>
    </div>
  );
};
export default ContentFilterGroupPage;
